/* ═══════════════════════════════════════════════════════════════
   NOVA — COSMOS CONTROLLER
   Mounts Milky Way overlay + constellation SVG + nova bursts
   
   GATING:
   • Theme setting: cosmos_intensity, constellations, nova_bursts
   • localStorage kill switch: nova_cosmos_off
   • Reduced motion: ALL disabled
   • Mobile: default OFF
   • requestIdleCallback: deferred init
   
   PERFORMANCE:
   • No continuous render loops
   • IntersectionObserver: mount only when visible
   • Burst events: throttled, max 1 per 400ms per element
   
   © 2025 Nova's Gems — Iteration 7
   ═══════════════════════════════════════════════════════════════ */

(() => {
  "use strict";

  const VERSION = "1.0.0";
  const STORE_KEY_OFF = "nova_cosmos_off";

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // GATING
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const mqReduce = () => window.matchMedia("(prefers-reduced-motion: reduce)");
  const mqMobile = () => window.matchMedia("(max-width: 768px)");

  function isOffByStorage() {
    try { return localStorage.getItem(STORE_KEY_OFF) === "1"; } catch (_) { return false; }
  }

  function getCosmosIntensity() {
    return document.documentElement.getAttribute("data-cosmos") || "off";
  }

  function getConstellationsSetting() {
    return document.documentElement.getAttribute("data-constellations") || "off";
  }

  function getBurstsSetting() {
    return document.documentElement.getAttribute("data-nova-bursts") || "off";
  }

  function enabled() {
    if (isOffByStorage()) return false;
    if (mqReduce().matches) return false;
    const intensity = getCosmosIntensity();
    return intensity !== "off";
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // MILKY WAY OVERLAY
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  function createMilkyWay() {
    const existing = document.querySelector('.nova-cosmos-overlay');
    if (existing) return;

    const overlay = document.createElement('div');
    overlay.className = 'nova-cosmos-overlay';
    overlay.setAttribute('aria-hidden', 'true');

    const milkyway = document.createElement('div');
    milkyway.className = 'nova-milkyway';

    overlay.appendChild(milkyway);
    document.body.appendChild(overlay);
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // CONSTELLATION SVG
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  function createConstellations() {
    const overlay = document.querySelector('.nova-cosmos-overlay');
    if (!overlay) return;

    const existing = overlay.querySelector('.nova-constellation-svg');
    if (existing) return;

    if (getConstellationsSetting() === "off") return;

    // Create simple SVG constellation pattern
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", "nova-constellation-svg");
    svg.setAttribute("viewBox", "0 0 1920 1080");
    svg.setAttribute("preserveAspectRatio", "xMidYMid slice");
    svg.setAttribute("aria-hidden", "true");

    // Simple constellation pattern (Orion-inspired)
    const stars = [
      { x: 400, y: 200, r: 2 },
      { x: 600, y: 300, r: 2.5 },
      { x: 500, y: 450, r: 1.5 },
      { x: 700, y: 500, r: 2 },
      { x: 800, y: 350, r: 1.8 },
      { x: 1200, y: 600, r: 2.2 },
      { x: 1400, y: 700, r: 1.6 },
      { x: 1300, y: 850, r: 2.4 },
    ];

    // Draw stars
    stars.forEach(star => {
      const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      circle.setAttribute("cx", star.x);
      circle.setAttribute("cy", star.y);
      circle.setAttribute("r", star.r);
      svg.appendChild(circle);
    });

    // Draw connecting lines (subset)
    const connections = [
      [0, 1], [1, 3], [3, 4], [5, 6], [6, 7]
    ];

    connections.forEach(([a, b]) => {
      const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
      line.setAttribute("x1", stars[a].x);
      line.setAttribute("y1", stars[a].y);
      line.setAttribute("x2", stars[b].x);
      line.setAttribute("y2", stars[b].y);
      svg.appendChild(line);
    });

    overlay.appendChild(svg);
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // NOVA BURSTS (on key interactions)
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const burstThrottle = new WeakMap(); // Throttle per element

  function spawnBurst(x, y) {
    if (getBurstsSetting() === "off") return;

    const burst = document.createElement('div');
    burst.className = 'nova-nova-burst';
    burst.style.left = `${x}px`;
    burst.style.top = `${y}px`;
    document.body.appendChild(burst);

    // Remove after animation
    setTimeout(() => {
      try { burst.remove(); } catch (_) {}
    }, 1000);
  }

  function bindBursts(root = document) {
    if (getBurstsSetting() === "off") return () => {};

    const ac = new AbortController();
    const { signal } = ac;

    // Bind to elements with data-nova-burst attribute
    const targets = root.querySelectorAll('[data-nova-burst]');

    targets.forEach(el => {
      el.addEventListener('click', (e) => {
        const now = Date.now();
        const lastBurst = burstThrottle.get(el) || 0;
        if (now - lastBurst < 400) return; // Throttle

        burstThrottle.set(el, now);
        spawnBurst(e.clientX, e.clientY);
      }, { passive: true, signal });
    });

    return () => ac.abort();
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // BOOTSTRAP
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  let burstCleanup = null;

  function init() {
    if (!enabled()) return;

    // Create overlays
    createMilkyWay();
    createConstellations();

    // Bind bursts
    burstCleanup = bindBursts(document);
  }

  function destroy() {
    // Remove overlay
    const overlay = document.querySelector('.nova-cosmos-overlay');
    if (overlay) {
      try { overlay.remove(); } catch (_) {}
    }

    // Cleanup bursts
    if (burstCleanup) {
      try { burstCleanup(); } catch (_) {}
      burstCleanup = null;
    }
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // LIFECYCLE
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  function start() {
    if (!enabled()) return;

    // Use requestIdleCallback for deferred init (performance)
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(init, { timeout: 2000 });
    } else {
      setTimeout(init, 100);
    }
  }

  function stop() {
    destroy();
  }

  // DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { if (enabled()) start(); }, { once: true });
  } else {
    if (enabled()) start();
  }

  // Theme Editor lifecycle
  document.addEventListener('shopify:section:load', () => {
    stop();
    if (enabled()) start();
  });

  document.addEventListener('shopify:section:unload', () => {
    stop();
  });

  // NovaMotion integration
  if (window.NovaMotion && typeof window.NovaMotion.registerModule === 'function') {
    window.NovaMotion.registerModule(
      'cosmos',
      () => { if (enabled()) start(); },
      () => stop()
    );
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PUBLIC API
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  if (typeof window !== 'undefined') {
    window.NovaCosmos = {
      __version: VERSION,
      start,
      stop,
      enabled,
      spawnBurst,
      setLocalStorageOff: () => { try { localStorage.setItem(STORE_KEY_OFF, "1"); stop(); } catch (_) {} },
      clearLocalStorageOff: () => { try { localStorage.removeItem(STORE_KEY_OFF); if (enabled()) start(); } catch (_) {} }
    };
  }
})();
